import { TourxLogo } from '../components/TourxLogo';

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <TourxLogo height={32} />
          </a>
          <a
            href="/login"
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Sign In
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: 16 June 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              TOURX (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is a transport management platform operated by Craner Kirkman Ltd,
              a company registered in England and Wales. We are committed to protecting the privacy and security of
              personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data
              Protection Act 2018.
            </p>
            <p className="text-gray-700 leading-relaxed mt-3">
              This privacy policy explains how we collect, use, store, and protect personal information when you use
              the TOURX portal at portal.tourx.uk and any associated mobile applications.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">2. Data Controller</h2>
            <p className="text-gray-700 leading-relaxed">
              The data controller responsible for your personal data is Craner Kirkman Ltd. If you have any questions
              about this privacy policy or our data practices, please contact us at:{' '}
              <a href="mailto:privacy@tourx.co.uk" className="text-blue-600 hover:underline">privacy@tourx.co.uk</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">3. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We collect and process the following categories of personal data:</p>

            <div className="ml-4 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">Account Information</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Name, email address, phone number, company name, and role within your organisation. This information
                  is provided when your account is created by an administrator or when you self-register.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Driver Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Driver card numbers, driving licence numbers, dates of birth, and issuing authority details extracted
                  from digital tachograph driver card data (DDD files). This data is uploaded by your employer for
                  compliance monitoring purposes.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Tachograph &amp; Activity Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Driving times, rest periods, working time, vehicle activity records, and infringement analysis derived
                  from tachograph card downloads. This includes data governed by EU Regulation 561/2006 and the Working
                  Time Directive (Road Transport) Regulations 2005.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Vehicle &amp; Fleet Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Vehicle registrations, VIN numbers, MOT/tax/calibration dates, PMI schedules, and walkaround check
                  records including photographs of vehicle defects.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Vehicle Tracking Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Where vehicle tracking integration is enabled by your employer, we process GPS location data, speed,
                  ignition status, and journey history provided by third-party tracking providers. This data is used
                  for fleet management, job allocation, and compliance monitoring.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Operational Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Job details, collection/delivery addresses, proof of delivery signatures and photographs, customer
                  contact information, and quotation records.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Mobile App Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  When using the TOURX Driver App, we collect: PIN login credentials (stored securely as a hash,
                  never in plain text); device tokens for push notifications; photographs taken during walkaround
                  vehicle checks using your device camera; proof of delivery (POD) signatures captured via the
                  touchscreen; POD photographs; GPS location data when submitting job status updates or proof of
                  delivery; and device identifiers for session management.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Technical Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  IP addresses, browser type, device information, and session tokens collected automatically when you
                  access the portal or mobile app. This data is used solely for authentication and security purposes.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">4. TOURX Driver App</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The TOURX Driver App is a mobile application designed for drivers to complete daily tasks. The app
              requires certain device permissions to function:
            </p>

            <div className="ml-4 space-y-3">
              <div>
                <h3 className="font-medium text-gray-900">Camera Access</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Required for photographing vehicle defects during walkaround checks and capturing proof of delivery
                  photographs. Photos are uploaded to our secure servers and associated with the relevant check or job
                  record. The app does not access your photo library or take photos without your action.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Location Services</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Used when submitting job status updates (e.g. arrived at collection, delivery completed) to record
                  the location of the event. Location is only captured at the point of submission, not continuously
                  tracked. This data helps your employer verify delivery locations and monitor operational efficiency.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Push Notifications</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Used to notify you of new job assignments, dispatch changes, and important operational updates from
                  your transport manager. You can disable push notifications in your device settings at any time.
                  We store a device token to deliver notifications; this token is deleted when you log out.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">PIN Authentication</h3>
                <p className="text-gray-700 text-sm mt-1">
                  The app offers PIN-based login for convenience. Your PIN is hashed using a secure one-way algorithm
                  before storage — we never store or transmit your PIN in plain text. PIN login sessions are tied
                  to your specific device.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Signature Capture</h3>
                <p className="text-gray-700 text-sm mt-1">
                  The app captures digital signatures for proof of delivery. Signatures are stored as images
                  associated with the relevant job record and are accessible to your employer for invoicing and
                  dispute resolution purposes.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Offline Data</h3>
                <p className="text-gray-700 text-sm mt-1">
                  The app may store limited data on your device for offline use, including your current job details
                  and walkaround checklist. This data is encrypted on-device and synced with our servers when an
                  internet connection is available. Logging out clears all locally stored data.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">5. Lawful Basis for Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We process personal data on the following legal bases:</p>

            <div className="ml-4 space-y-3">
              <div>
                <h3 className="font-medium text-gray-900">Legal Obligation</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Processing of tachograph data, drivers&apos; hours records, and infringement management is required to
                  comply with EU Regulation 561/2006, the Transport Act 1968, and the Road Transport (Working Time)
                  Regulations 2005. Operators have a legal duty to download, store, and analyse driver card data.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Legitimate Interests</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Fleet management, vehicle compliance tracking, operational planning, and business administration
                  are processed on the basis of our legitimate interests and those of your employer in running a
                  safe and compliant transport operation.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-gray-900">Performance of a Contract</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Account management and service delivery are necessary for the performance of our contract with
                  your employer.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">6. How We Use Your Data</h2>
            <p className="text-gray-700 leading-relaxed">
              We use your personal data to: provide and maintain the TOURX portal and Driver App; analyse
              tachograph data and identify drivers&apos; hours infringements; manage vehicle compliance schedules
              (MOT, tax, PMI, calibration); facilitate walkaround vehicle checks and defect tracking via the mobile
              app including photograph capture; support operational planning including job allocation, dispatch, and
              live tracking; process proof of delivery signatures and photographs; deliver push notifications for
              job assignments and status updates; generate compliance reports for your employer; send account
              notifications such as password resets and infringement letters; and improve the security and
              performance of our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">7. Data Sharing</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We do not sell your personal data. We may share data in the following circumstances:
            </p>
            <div className="ml-4 space-y-3">
              <div>
                <h3 className="font-medium text-gray-900">Within Your Organisation</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Your employer&apos;s administrators and transport managers can access your data within the portal
                  as part of their compliance and fleet management responsibilities.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Service Providers</h3>
                <p className="text-gray-700 text-sm mt-1">
                  We use trusted third-party providers for hosting (Railway), email delivery (Resend), and frontend
                  hosting (Vercel). These providers process data on our behalf under appropriate data processing
                  agreements.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-900">Legal Requirements</h3>
                <p className="text-gray-700 text-sm mt-1">
                  We may disclose data where required by law, including to the DVSA (Driver and Vehicle Standards
                  Agency) or the Traffic Commissioner during compliance investigations.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              Tachograph card data and drivers&apos; hours records are retained for a minimum of 12 months as
              required by EU Regulation 561/2006, and typically for 24 months in line with DVSA best practice
              guidance. Vehicle compliance records are retained for the operational life of the vehicle plus 12
              months. Account data is retained for as long as the account remains active. Upon account closure or
              at the request of your employer, personal data will be securely deleted within 90 days, except where
              longer retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">9. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organisational measures to protect your personal data, including:
              encryption of data in transit using TLS/HTTPS; secure password hashing using bcrypt; role-based access
              controls limiting data visibility to authorised users; session management with JWT tokens; and regular
              review of our security practices.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">10. International Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Our hosting infrastructure may process data outside the UK. Where data is transferred internationally,
              we ensure appropriate safeguards are in place, including Standard Contractual Clauses approved by the
              Information Commissioner&apos;s Office (ICO), or transfers to countries with an adequacy decision.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">11. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Under the UK GDPR, you have the following rights regarding your personal data:
            </p>
            <div className="ml-4 space-y-2">
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right of Access</span> — You can request a copy of the personal data we hold about you.
              </p>
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right to Rectification</span> — You can request correction of inaccurate or incomplete data.
              </p>
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right to Erasure</span> — You can request deletion of your data, subject to legal retention requirements.
              </p>
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right to Restrict Processing</span> — You can request that we limit how we use your data.
              </p>
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right to Data Portability</span> — You can request your data in a machine-readable format.
              </p>
              <p className="text-gray-700 text-sm">
                <span className="font-medium text-gray-900">Right to Object</span> — You can object to processing based on legitimate interests.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mt-3">
              To exercise any of these rights, please contact{' '}
              <a href="mailto:privacy@tourx.co.uk" className="text-blue-600 hover:underline">privacy@tourx.co.uk</a>.
              We will respond within one month. Note that where data processing is required to meet your employer&apos;s
              legal obligations under transport legislation, some requests may be declined or deferred.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">12. Cookies</h2>
            <p className="text-gray-700 leading-relaxed">
              The TOURX portal uses essential cookies and local storage for authentication (session tokens) only.
              We do not use advertising cookies, analytics tracking cookies, or third-party marketing cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">13. Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this privacy policy from time to time. Any changes will be posted on this page with an
              updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3">14. Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are unhappy with how we handle your personal data, you have the right to lodge a complaint with
              the Information Commissioner&apos;s Office (ICO). You can contact the ICO at{' '}
              <a href="https://ico.org.uk" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>{' '}
              or by calling 0303 123 1113. We would, however, appreciate the opportunity to address your concerns
              first — please contact us at{' '}
              <a href="mailto:privacy@tourx.co.uk" className="text-blue-600 hover:underline">privacy@tourx.co.uk</a>.
            </p>
          </section>

          <section className="border-t border-gray-200 pt-6 mt-10">
            <p className="text-sm text-gray-500">
              Craner Kirkman Ltd &bull; TOURX Transport Management Portal &bull; portal.tourx.uk
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
